import { supabase } from '../supabase'

// Every unpaid church gets this many free sermon scans before the paywall.
export const FREE_SCAN_LIMIT = 5

// Gate any scan operation. Reads the church's current usage + paid flag and
// decides whether `videosToScan` more scans are allowed. Paid churches are
// always allowed; unpaid churches are allowed up to FREE_SCAN_LIMIT total.
//
// Returns { allowed, remaining?, message? }. `remaining` is how many free
// scans are left BEFORE this operation. On the blocked path a human-readable
// `message` is included for the paywall UI.
export async function canScan(churchId, videosToScan = 1) {
  if (!churchId) return { allowed: false, remaining: 0, message: 'No church selected.' }

  let { data: church, error } = await supabase
    .from('churches')
    .select('free_scans_used, is_paid, scan_limit')
    .eq('id', churchId)
    .single()
  // Tolerate the scan_limit column not existing yet (migration not run).
  if (error && /scan_limit/i.test(error.message || '')) {
    ;({ data: church, error } = await supabase
      .from('churches')
      .select('free_scans_used, is_paid')
      .eq('id', churchId)
      .single())
  }

  // If we can't read the row, fail open for paid-style behaviour would be
  // risky — fail closed but with a clear message so the caller can surface it.
  if (error || !church) {
    return { allowed: false, remaining: 0, message: 'Could not check your scan allowance. Please try again.' }
  }

  if (church.is_paid) return { allowed: true }

  // Per-church allowance, floored at the global minimum.
  const limit = Math.max(FREE_SCAN_LIMIT, church.scan_limit || FREE_SCAN_LIMIT)
  const remaining = limit - (church.free_scans_used || 0)
  if (remaining >= videosToScan) return { allowed: true, remaining }

  return {
    allowed: false,
    remaining,
    message: remaining <= 0
      ? `You've used all ${limit} sermon scans`
      : `You have ${remaining} scan${remaining === 1 ? '' : 's'} remaining — this import needs ${videosToScan}`,
  }
}

// Increment a church's used-scan counter by `count`. Reads the current value
// first (no atomic RPC available) and writes back the new total. Best-effort:
// returns { error } on failure so callers can log without blocking the user.
export async function recordScans(churchId, count = 1) {
  if (!churchId || count <= 0) return { error: null }
  const { data: church, error: readErr } = await supabase
    .from('churches')
    .select('free_scans_used')
    .eq('id', churchId)
    .single()
  if (readErr) return { error: readErr }
  const next = (church?.free_scans_used || 0) + count
  const { error } = await supabase
    .from('churches')
    .update({ free_scans_used: next })
    .eq('id', churchId)
  return { error, free_scans_used: next }
}
